import { Plus } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { userHasAccess } from "@/access-control/permissions";
import usePermissions from "@/access-control/queries/usePermissions";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import useUsers from "@/hooks/useUsers";
import { isTransformationSetServerSideOnly } from "@/transforms/components/TransformsTable";
import { TransformDefinitionSet } from "@/transforms/transformationDefinition";
import { loadTransforms } from "@/transforms/transformations";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { EnableDestinationStepProps } from "../stepProps";
import { NewTransformationSetForm } from "../transformations/NewTransformationSetForm";
import { TransformationSetItem } from "../transformations/TransformationSetItem";
import { useEventsForDestination } from "../useEventsForDestination";

function useCanEditTransformations(): boolean {
  const { currentUser } = useUsers();
  const { data: rolePermissions } = usePermissions();
  return userHasAccess(
    currentUser,
    rolePermissions,
    RBACDomain.eventLibrary,
    RBACAccessLevel.write,
  );
}

export function TransformationsStep({
  data,
  context,
  goNext,
}: EnableDestinationStepProps) {
  const { saasDestination: destination, configuredDestination } = context;
  const [sets, setSets] = useState<TransformDefinitionSet[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [expandedSetId, setExpandedSetId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canEdit = useCanEditTransformations();
  const events = useEventsForDestination(data, context);

  const reload = useCallback(() => {
    setLoadFailed(false);
    loadTransforms(setSets, setLoaded, (error) => {
      console.error(error);
      setLoadFailed(true);
    });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const destinationSets = useMemo(
    () =>
      sets.filter(
        (set) => !!destination && set.destination?.key === destination.key,
      ),
    [sets, destination],
  );

  const serverDestinationKeys = useMemo(
    () =>
      destination && configuredDestination?.connection_mode === "server"
        ? { [destination.key]: configuredDestination.id }
        : {},
    [destination, configuredDestination],
  );

  return (
    <WizardStepLayout
      title="Add transformations"
      description={`Optional. Rename events or reshape properties before they reach ${destination?.name ?? "this destination"}.`}
      primaryAction={{ label: "Continue", onClick: goNext }}
    >
      {loadFailed && (
        <Banner type="error">
          We couldn’t load transformations. You can continue without them, or{" "}
          <button
            type="button"
            onClick={reload}
            className="cursor-pointer underline"
          >
            try again
          </button>
          .
        </Banner>
      )}
      {!loaded ? (
        !loadFailed && <LoadingSpinner className="size-6" />
      ) : destinationSets.length === 0 ? (
        <Typography variant="body-sm" color="subtle">
          No transformations apply to {destination?.name} yet. Most destinations
          work without one.
        </Typography>
      ) : (
        <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
          {destinationSets.map((set) => (
            <TransformationSetItem
              key={set.id}
              transformationSet={set}
              isOpen={expandedSetId === set.id}
              onToggle={() =>
                setExpandedSetId((id) => (id === set.id ? null : set.id))
              }
              isEditable={canEdit}
              isServerSideOnly={isTransformationSetServerSideOnly(
                set,
                serverDestinationKeys,
              )}
              onChange={reload}
            />
          ))}
        </ul>
      )}
      {loaded && canEdit && destination && (
        <div>
          {creating ? (
            <NewTransformationSetForm
              destinationKey={destination.key}
              events={events}
              existingSets={destinationSets}
              onCreated={(id) => {
                setCreating(false);
                setExpandedSetId(id);
                reload();
              }}
              onOpenExisting={(id) => {
                setCreating(false);
                setExpandedSetId(id);
              }}
              onCancel={() => setCreating(false)}
            />
          ) : (
            <Button
              variant="outline"
              leadingIcon={<Plus aria-hidden />}
              onClick={() => setCreating(true)}
            >
              Add transformation
            </Button>
          )}
        </div>
      )}
      {destination && (
        <Typography variant="body-sm" color="subtle">
          To map events to {destination.name} standard events or see
          transformations shared with other destinations, use the{" "}
          <WizardLink
            to={`/transformations/destination/${destination.slug}`}
            className="text-textActionSelected cursor-pointer underline"
          >
            transformations page
          </WizardLink>
          .
        </Typography>
      )}
    </WizardStepLayout>
  );
}
